import { useEffect } from "react";
import { useT, type Lang } from "../i18n";
import { tg } from "../telegram";
import { ttsSupported } from "../speech";
import ClauseTable from "./ClauseTable";
import type { Clause } from "../types";

interface Props {
  clause: Clause;
  lang: Lang;
  onClose: () => void;
  onShare: () => void;
  onSpeak: () => void;
  speaking: boolean;
}

const TTS_ON = ttsSupported();

export function clauseText(c: Clause, lang: Lang): string {
  const v = lang === "ru" ? c.text_ru : lang === "ps" ? c.text_ps : lang === "fa" ? c.text_fa : c.text_en;
  return v || c.text_en || "";
}

export function clauseHeading(c: Clause, lang: Lang): string {
  const v = lang === "ru" ? c.heading_ru : lang === "ps" ? c.heading_ps : lang === "fa" ? c.heading_fa : c.heading;
  return v || c.heading || "";
}

export function docTitle(d: { title_en: string; title_ru: string; title_ps?: string; title_fa?: string }, lang: Lang): string {
  const v = lang === "ru" ? d.title_ru : lang === "ps" ? d.title_ps : lang === "fa" ? d.title_fa : d.title_en;
  return v || d.title_en;
}

function Paragraphs({ text }: { text: string }) {
  const parts = text.split(/\n{2,}/).map((s) => s.trim()).filter(Boolean);
  return (
    <>
      {parts.map((p, i) => (
        <p key={i}>{p}</p>
      ))}
    </>
  );
}

function Subtree({ clause, lang, depth }: { clause: Clause; lang: Lang; depth: number }) {
  const heading = clauseHeading(clause, lang);
  const text = clauseText(clause, lang);
  return (
    <div className="reader-node" style={{ marginInlineStart: depth ? 12 : 0 }}>
      {(clause.number || heading) && (
        <div className="reader-node-head">
          {clause.number && <span className="clause-num">{clause.number}</span>}
          {heading && <span className="reader-heading">{heading}</span>}
        </div>
      )}
      {text && <Paragraphs text={text} />}
      {clause.table && <ClauseTable table={clause.table} lang={lang} />}
      {clause.children.map((ch) => (
        <Subtree key={ch.id} clause={ch} lang={lang} depth={depth + 1} />
      ))}
    </div>
  );
}

export default function ClauseReader({ clause, lang, onClose, onShare, onSpeak, speaking }: Props) {
  const { t } = useT();
  useEffect(() => {
    tg()?.HapticFeedback?.impactOccurred("light");
  }, []);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="reader-scroll">
          <Subtree clause={clause} lang={lang} depth={0} />
        </div>
        <div className="reader-actions">
          {TTS_ON && (
            <button
              className={"btn small" + (speaking ? " speaking" : "")}
              onClick={onSpeak}
            >
              {speaking ? `⏹ ${t("clause.stop")}` : `🔊 ${t("clause.speak")}`}
            </button>
          )}
          <button className="btn small" onClick={onShare}>
            🔗 {t("share.button")}
          </button>
          <button className="btn secondary small" onClick={onClose}>
            {t("common.close")}
          </button>
        </div>
      </div>
    </div>
  );
}
